import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { PoNotificationService } from '@po-ui/ng-components';
import { AuthService } from '../guards/auth.service';
import { HomeService } from '../home/home.service';
import { environment } from '../../environments/environment';
import { firstValueFrom } from 'rxjs';

@Component({
  selector: 'app-sso',
  templateUrl: './sso.component.html',
  styleUrls: ['./sso.component.css'],
  standalone: false,
})
export class SsoComponent implements OnInit, OnDestroy {
  public loading: boolean = true;
  public loadingMessage: string = 'Autenticando e preparando seu acesso aos orçamentos...';

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private http: HttpClient,
    private authService: AuthService,
    private homeService: HomeService,
    private poNotification: PoNotificationService
  ) {}

  async ngOnInit(): Promise<void> {
    const ssoToken = this.route.snapshot.queryParams['token'];
    const targetRoute = this.route.snapshot.queryParams['target'] || '/orcamento';

    if (!ssoToken) {
      this.loading = false;
      this.poNotification.error('Token de autenticação não informado.');
      this.router.navigate(['/login']);
      return;
    }

    try {
      const response = await firstValueFrom(
        this.http.post<any>(`${environment.API}sso/validate`, { token: ssoToken })
      );

      if (response && response.success && response.session) {
        const session = response.session;
        const segmento = session.Segmento || 'PECAS';
        const cgc = (session.cgc || '').trim();
        const codeuser = session.codeuser;

        // 1. Gravar dados primários da autenticação no sessionStorage
        sessionStorage.setItem('token', session.token);
        sessionStorage.setItem('user', session.user);
        sessionStorage.setItem('userName', session.userName);
        sessionStorage.setItem('userVendedor', session.userVendedor || '');
        sessionStorage.setItem('codeuser', session.codeuser);
        sessionStorage.setItem('tipoUser', session.tipoUser);
        sessionStorage.setItem('Segmento', segmento);
        sessionStorage.setItem('cgc', cgc);
        sessionStorage.setItem('tabprc', session.tabprc || 'P01');
        sessionStorage.setItem('tpop', session.tpop || '01');
        sessionStorage.setItem('tabesp', session.tabesp || '');
        sessionStorage.setItem('empresa', session.empresa || '0103');
        sessionStorage.setItem('cpg', session.cpg || '');
        sessionStorage.setItem('cpcred', session.cpcred || '');
        sessionStorage.setItem('cppix', session.cppix || '');
        sessionStorage.setItem('vend1', session.vend1 || '');
        sessionStorage.setItem('codcli', session.codcli || '');
        sessionStorage.setItem('lojacli', session.lojacli || '');
        sessionStorage.setItem('ufcli', session.ufcli || '');

        this.loadingMessage = 'Carregando configurações e dados da empresa...';

        // 2. Hidratar dados exatamente como o HomeComponent.isOk() faz:
        // 2a. Loja do cliente (codcli, lojacli, ufcli)
        try {
          const lojaRes = await firstValueFrom(this.homeService.getLoja(cgc, segmento));
          if (lojaRes && lojaRes.length > 0) {
            const targetLoja = (session.lojacli || '').trim();
            const targetCod = (session.codcli || '').trim();
            const cli = (targetLoja ? lojaRes.find((l: any) => String(l.loja).trim() === targetLoja) : null)
              || (targetCod ? lojaRes.find((l: any) => String(l.codigo).trim() === targetCod) : null)
              || lojaRes[0];
            sessionStorage.setItem('codcli', cli.codigo);
            sessionStorage.setItem('lojacli', cli.loja);
            sessionStorage.setItem('ufcli', cli.uf);
          }
        } catch (e) {
          console.warn('SSO: aviso ao consultar loja do cliente:', e);
        }

        // 2b. Tabelas de preço do segmento
        try {
          const seguimentosRes = await firstValueFrom(this.homeService.getSeguimentos(codeuser, segmento));
          if (seguimentosRes && Array.isArray(seguimentosRes)) {
            const targetCod = (session.codcli || '').trim();
            const segItem = seguimentosRes.find((s: any) =>
              (s.cgc || '').trim() === cgc &&
              (s.segmto || '').trim() === segmento &&
              (targetCod ? String(s.codigo || '').trim() === targetCod : true)
            ) || seguimentosRes.find((s: any) => (s.cgc || '').trim() === cgc && (s.segmto || '').trim() === segmento)
              || seguimentosRes.find((s: any) => (s.cgc || '').trim() === cgc);
            if (segItem) {
              if (segItem.tabprc) sessionStorage.setItem('tabprc', segItem.tabprc);
              if (segItem.tpop) sessionStorage.setItem('tpop', segItem.tpop);
              if (segItem.tabesp) sessionStorage.setItem('tabesp', segItem.tabesp);
            }
          }
        } catch (e) {
          console.warn('SSO: aviso ao consultar tabela de preço:', e);
        }

        // 2c. Parâmetros da empresa (MV_CRMPECA, MV_CPECAS, MV_CCCREDP, MV_CPPIXP)
        try {
          const allParams = await firstValueFrom(this.homeService.getParams(segmento, '0301'));
          if (allParams) {
            if (allParams.crm?.descricao) sessionStorage.setItem('empresa', allParams.crm.descricao);
            if (allParams.pagto?.descricao) sessionStorage.setItem('cpg', allParams.pagto.descricao);
            if (allParams.cartao?.descricao) sessionStorage.setItem('cpcred', allParams.cartao.descricao);
            if (allParams.pix?.descricao) sessionStorage.setItem('cppix', allParams.pix.descricao);
          }
        } catch (e) {
          console.warn('SSO: aviso ao consultar parâmetros da empresa:', e);
        }

        // 2d. Vendedor padrão (MV_CVENDP)
        try {
          const vend1Res = await firstValueFrom(this.homeService.getVend1(segmento));
          if (vend1Res?.CONTEUDO) {
            sessionStorage.setItem('vend1', vend1Res.CONTEUDO);
          }
        } catch (e) {
          console.warn('SSO: aviso ao consultar vendedor padrão:', e);
        }

        this.loading = false;

        // 3. Atualizar o estado global da aplicação
        this.authService.setMostrarMenu(true);
        this.authService.setTipoUser(session.tipoUser === 'U');

        this.poNotification.setDefaultDuration(2500);
        this.poNotification.success(`Bem-vindo, ${session.userName}! Redirecionando...`);

        // 4. Redirecionar para o destino desejado (/orcamento)
        const destination = targetRoute || '/orcamento';
        this.router.navigate([destination]);
      } else {
        this.loading = false;
        this.poNotification.error('Falha ao autenticar via SSO. Por favor faça login.');
        this.router.navigate(['/login']);
      }
    } catch (err: any) {
      this.loading = false;
      console.error('Erro na validação do SSO:', err);
      const errorMsg =
        err?.error?.error || 'Token de acesso expirado ou inválido. Faça login manualmente.';
      this.poNotification.error(errorMsg);
      this.router.navigate(['/login']);
    }
  }

  ngOnDestroy(): void {
    this.loading = false;
  }
}
